// 材料搬入表 (NEXCO対応) — 発注者提出系
// 行=材料、列=第N回搬入(日付別) のマトリクス形式。受領サンプル準拠。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { buildLookups, downloadBlob, todayStr, setHeaderRow, fmtDate, sumContentKg } from "./helpers";

function resolveWork(db: ExtendedDB, lk: ReturnType<typeof buildLookups>, workId?: number) {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

function fmtDesignUsage(standardUsage: number | null | undefined, unit: string | null | undefined): string {
  if (standardUsage != null) {
    return `${standardUsage}${unit ?? ""}`;
  }
  return "";
}

function fmtTotalWeight(weightKg: number): string {
  if (weightKg > 0) {
    return `${weightKg.toFixed(1)}kg`;
  }
  return "";
}

export async function exportMaterialDeliveryTable(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveWork(db, lk, workId);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";
  const ws = wb.addWorksheet("材料搬入表");

  ws.getCell("A1").value = work?.official_name ?? "材料搬入表";
  ws.getCell("A1").font = { bold: true, size: 12 };
  ws.getCell("A2").value = "材料搬入表";
  ws.getCell("A2").font = { bold: true, size: 11 };
  ws.getCell("A3").value = `出力日: ${todayStr()}`;
  ws.getCell("A3").font = { size: 10, italic: true };

  // 当該工事の CarryIn を日付昇順
  const carryIns = db.carryIns
    .filter((c) => c.deleted_at == null && (!work || c.work_id === work.id))
    .filter((c) => c.received_at != null)
    .sort((a, b) => (a.received_at ?? 0) - (b.received_at ?? 0));

  if (carryIns.length === 0) {
    ws.getCell("A5").value = "※ 対象工事の搬入データが存在しません";
    ws.getCell("A5").font = { italic: true };
    const bufEmpty = await wb.xlsx.writeBuffer();
    downloadBlob(bufEmpty, `材料搬入表_${todayStr()}.xlsx`);
    return;
  }

  // 搬入日(重複除外+昇順)
  const deliveryDates = Array.from(new Set(carryIns.map((c) => fmtDate(c.received_at)))).sort();

  type MatRow = {
    materialName: string;
    materialDetailName: string;
    standardName: string;
    packageForm: string;
    designUsage: string;
    cells: Map<string, { lotNo: string; manufacturedAt: string; rangeStart?: number; rangeEnd?: number; count: number; weightKg: number }>;
    totalCount: number;
    totalWeightKg: number;
  };
  const rowsMap = new Map<string, MatRow>();

  for (const c of carryIns) {
    const d = fmtDate(c.received_at);
    const qrs = db.materialQrs.filter((q) => q.carry_in_id === c.id);
    const qrsByLot = new Map<string, typeof qrs>();
    for (const q of qrs) {
      if (!q.lot_id) continue;
      const arr = qrsByLot.get(q.lot_id) ?? [];
      arr.push(q);
      qrsByLot.set(q.lot_id, arr);
    }
    for (const [lotId, qrGroup] of qrsByLot) {
      const lot = lk.lotById.get(lotId);
      if (!lot || lot.material_id == null) continue;
      const material = lk.materialById.get(lot.material_id);
      if (!material) continue;
      const details = lk.materialDetailsByMaterialId.get(material.id) ?? [];
      const detailNames = details.map((dd) => dd.detail_name).join("+");
      const key = `${material.id}-${detailNames}`;
      let row = rowsMap.get(key);
      if (!row) {
        row = {
          materialName: material.name,
          materialDetailName: material.usage_category,
          standardName: material.standard_name,
          packageForm: material.package_form,
          designUsage: fmtDesignUsage(material.standard_usage, material.standard_usage_unit),
          cells: new Map(),
          totalCount: 0,
          totalWeightKg: 0,
        };
        rowsMap.set(key, row);
      }
      const ids = qrGroup.map((qr) => qr.id).sort((a, b) => a - b);
      const rangeStart = ids[0];
      const rangeEnd = ids[ids.length - 1];
      const count = qrGroup.length;
      const contentKgs = sumContentKg(details);
      const weightKg = count * contentKgs;
      row.cells.set(d, {
        lotNo: lot.lot_no ?? "",
        manufacturedAt: lot.manufactured_at != null ? fmtDate(lot.manufactured_at) : "",
        rangeStart,
        rangeEnd,
        count,
        weightKg,
      });
      row.totalCount += count;
      row.totalWeightKg += weightKg;
    }
  }

  const matRows = Array.from(rowsMap.values()).sort((a, b) => a.materialName.localeCompare(b.materialName));

  const fixedCols = 6;
  const headerRowIdx = 5;
  ws.getCell(headerRowIdx, 1).value = "名称";
  ws.getCell(headerRowIdx, 2).value = "荷姿";
  ws.getCell(headerRowIdx, 3).value = "規格・材料名";
  ws.getCell(headerRowIdx, 4).value = "設計数量";
  ws.getCell(headerRowIdx, 5).value = "標準使用量";
  ws.getCell(headerRowIdx, 6).value = "設計使用量";
  deliveryDates.forEach((dateStr, i) => {
    const col = fixedCols + i + 1;
    ws.getCell(headerRowIdx, col).value = `第${i + 1}回搬入\n(${dateStr})\n管理番号`;
  });
  const totalCol = fixedCols + deliveryDates.length + 1;
  ws.getCell(headerRowIdx, totalCol).value = "搬入\n合計";
  [12, 18, 30, 12, 14, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
  for (let i = 0; i < deliveryDates.length; i++) ws.getColumn(fixedCols + i + 1).width = 18;
  ws.getColumn(totalCol).width = 16;
  setHeaderRow(ws, headerRowIdx);
  ws.getRow(headerRowIdx).height = 48;

  let r = headerRowIdx + 1;
  for (const matRow of matRows) {
    const baseR = r;
    ws.getCell(baseR, 1).value = matRow.materialDetailName;
    ws.getCell(baseR + 1, 2).value = matRow.packageForm;
    ws.getCell(baseR, 3).value = matRow.standardName;
    ws.getCell(baseR + 1, 3).value = matRow.materialName;
    ws.getCell(baseR + 1, 3).font = { italic: true };
    ws.getCell(baseR + 2, 3).value = "LotNo.";
    ws.getCell(baseR + 3, 3).value = "(製造年月日)";
    ws.getCell(baseR + 4, 3).value = "(メーカー仕様)";
    ws.getCell(baseR + 4, 3).font = { size: 8 };

    deliveryDates.forEach((dateStr, i) => {
      const col = fixedCols + i + 1;
      const cell = matRow.cells.get(dateStr);
      if (!cell) return;
      ws.getCell(baseR, col).value = `${cell.count}個`;
      if (cell.rangeStart != null && cell.rangeEnd != null) {
        ws.getCell(baseR + 1, col).value = `${cell.rangeStart}-${cell.rangeEnd}`;
      }
      ws.getCell(baseR + 2, col).value = cell.lotNo;
      if (cell.manufacturedAt) ws.getCell(baseR + 3, col).value = `(${cell.manufacturedAt})`;
    });

    ws.getCell(baseR, totalCol).value = `${matRow.totalCount}個`;
    ws.getCell(baseR, totalCol).font = { bold: true };
    ws.getCell(baseR + 1, totalCol).value = fmtTotalWeight(matRow.totalWeightKg);
    ws.getCell(baseR + 1, totalCol).font = { bold: true };

    for (let i2 = 0; i2 < 5; i2++) {
      const rowEl = ws.getRow(baseR + i2);
      rowEl.eachCell({ includeEmpty: true }, (c2) => {
        c2.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      });
    }

    r += 5;
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `材料搬入表_${todayStr()}.xlsx`);
}
